import {api} from "@/api/api";
import {FUNNEL_TRUST_PILOT} from "@/types/funnelTypes";
import {reportToFlare} from "@/utils/reportToFlare";
import type {OrderCreationResponse} from "@/types/trackingApiTypes";
import {REPUTECH_DOMAIN} from "@/utils/constants/brand";
import { retryIdempotentRequest } from "@/utils/http/serviceAvailability";
import { isReputechTrustpilotPriceTier } from "@/utils/priceTierHelper";
import type {
  EmailBlacklistResponse,
  EmailWhitelistResponse,
  PriceTierType,
} from "@/types/trustpilotPricing";

export type TrustPilotSearchRequest = {
  query: string;
  limit: number;
  cursor?: string;
};

const LEGACY_TRUSTPILOT_SEARCH_REPORT_STORAGE_KEY =
  "reputech:trustpilot-search-legacy-data-reported";

const shouldReportLegacyTrustPilotSearchResponse = () => {
  if (typeof window === "undefined") {
    return true;
  }

  try {
    if (
      window.sessionStorage.getItem(LEGACY_TRUSTPILOT_SEARCH_REPORT_STORAGE_KEY)
    ) {
      return false;
    }

    window.sessionStorage.setItem(
      LEGACY_TRUSTPILOT_SEARCH_REPORT_STORAGE_KEY,
      "1",
    );
    return true;
  } catch {
    return true;
  }
};

/** Keep Axios rejection details available to Trustpilot search and order callers. */
const reviewTrustPilotApi = {
  async get(data: TrustPilotSearchRequest) {
    const response = await api.get<SearchBusinessTrustPilotApiResponseType>(
      "/trust-pilot/search",
      {
        params: data,
      },
    );
    const payload = response.data;
    let businessUnits: SearchBusinessTrustPilotResultType[] = [];

    if (Array.isArray(payload.businessUnits)) {
      businessUnits = payload.businessUnits;
    } else if (Array.isArray(payload.data)) {
      // TODO(#216): remove the legacy data branch once every backend
      // environment returns businessUnits for trust-pilot search responses.
      if (shouldReportLegacyTrustPilotSearchResponse()) {
        reportToFlare(
          new Error("Trustpilot search used legacy data response branch"),
          "trustpilot_search_legacy_data_branch",
        );
      }
      businessUnits = payload.data;
    } else {
      reportToFlare(
        new Error("Trustpilot search returned an unexpected response shape"),
        "trustpilot_search_unexpected_response_shape",
      );
    }

    return {
      businessUnits,
      // Missing success is treated as true so older environments that only
      // returned units keep working. Explicit false is a failed lookup.
      success: payload.success !== false,
    };
  },
  async create(data: TrustPilotOrderDataPayload, signal?: AbortSignal) {
    const url = "trust-pilot/orders";
    const response = await api.post<TrustPilotOrderResponse>(url, data, {
      params: {frontend_domain: REPUTECH_DOMAIN},
      signal,
    });
    return response.data;
  },
  async getCompanyDetails(businessId: string) {
    const response = await api.get<GetTrustPilotBusinessDetailsType>(
      `trust-pilot/${businessId}`,
    );

    return response.data.data;
  },
  async getReviews(
    {
      companyId,
      page,
      sort,
    }: { companyId: string; page?: number; sort?: string },
  ) {
    const params: Record<string, number | string | undefined> = {
      page,
      sort,
    };

    const response = await api.get<GetTrustPilotBusinessReviewsType>(
      `trust-pilot/${companyId}/reviews`,
      { params },
    );

    return response.data;
  },
  async getPriceTier() {
    return retryIdempotentRequest(async () => {
      const response = await api.get<PriceTierType>("price-tier", {
        params: {type: "trust-pilot", frontend_domain: REPUTECH_DOMAIN},
      });
      if (!isReputechTrustpilotPriceTier(response.data)) {
        throw new Error("The API did not return active Reputech Trustpilot pricing.");
      }

      return response.data;
    });
  },
  async checkCoupon(couponCode: string, businessIds: string[] = []) {
    return api.get("coupons/check-code", {
      params: {
        code: couponCode,
        ...(businessIds.length ? {business_ids: businessIds} : {}),
        platform: FUNNEL_TRUST_PILOT,
        frontend_domain: REPUTECH_DOMAIN,
      },
    });
  },
  async checkIfEmailIsWhiteListed(
    email: string,
    businessIds: string[] = [],
  ): Promise<EmailWhitelistResponse> {
    const response = await api.get("email/is-whitelisted", {
      params: {email, ...(businessIds.length ? {business_ids: businessIds} : {})},
    });
    return response.data;
  },
  async checkIfEmailIsBlackListed(
    email: string,
    businessIds: string[] = [],
  ): Promise<EmailBlacklistResponse> {
    const response = await api.get("email/is-blacklisted", {
      params: {email, ...(businessIds.length ? {business_ids: businessIds} : {})},
    });
    return response.data;
  },
  async sendCartShareData(data: TrustPilotCartShareDataPayload) {
    // Trustpilot has its own API wrapper, so the backend funnel name is fixed
    // here instead of passed from every Trustpilot caller.
    const response = await api.post<TrustPilotCartShareResponse>(`cart-share`, {
      name: FUNNEL_TRUST_PILOT,
      payload: data,
    }, {params: {frontend_domain: REPUTECH_DOMAIN}});

    return response.data;
  },
  async getCartShareData(uuid: string) {
    const response = await api.get<TrustPilotCartShareResponse>(
      `cart-share/${uuid}`,
      {params: {frontend_domain: REPUTECH_DOMAIN}},
    );

    return response.data;
  },
};

export default reviewTrustPilotApi;

type TrustPilotOrderUserPayload = {
  name: string;
  lastName: string;
  email: string;
  phone: string;
  // German keys are part of the backend order contract.
  firma: string;
  salutation: string;
  plz: string;
  location: string;
  street: string;
    coupon_used?: string | null;
    newsletterOptIn?: boolean;
    businessCustomerConfirmed?: boolean;
    termsAccepted?: boolean;
    reviewSubmissionAuthorized?: boolean;
    successBasedBillingAccepted?: boolean;
};

export type TrustPilotOrderDataPayload = {
  frontend_domain: string;
  // Backend contract name is business_id, but Trustpilot orders use the domain.
  business_id: string;
  review_ids: string[];
  user: TrustPilotOrderUserPayload;
  live_chat_id?: string;
  /**
   * Sticky per-checkout key (utils/analytics/purchaseIdempotency) so the
   * backend can collapse duplicate creates from reloads/payment returns
   * instead of producing a second order. Same contract as the Google funnel.
   */
  idempotency_key?: string;
};

type TrustPilotOrderResponseData = OrderCreationResponse;

export type TrustPilotOrderResponse = {
  success: boolean;
  message?: string;
  data?: TrustPilotOrderResponseData;
} & OrderCreationResponse;

export type SearchBusinessTrustPilotResponseType = {
  businessUnits: SearchBusinessTrustPilotResultType[];
  success: boolean;
};

type SearchBusinessTrustPilotApiResponseType = {
  // Trustpilot search has returned both `businessUnits` and `data` arrays
  // while backend response shapes have been rolled out across environments.
  success?: boolean;
  businessUnits?: SearchBusinessTrustPilotResultType[];
  data?: SearchBusinessTrustPilotResultType[];
};

export type SearchBusinessTrustPilotResultType = {
  businessUnitId: string;
  stars: number;
  identifyingName: string;
  displayName: string;
  logoUrl: string;
  numberOfReviews: number;
  trustScore: number;
  location: TrustPilotBusinessLocationType;
  isRecommendedInCategories: boolean;
  contact: TrustPilotBusinessContactType;
  categories: TrustPilotBusinessCategoryType[];
};

export type TrustPilotBusinessLocationType = {
  address: string;
  city: string;
  zipCode: string;
  country: string;
};

export type TrustPilotBusinessContactType = {
  website: string;
  email: string;
  phone: string;
};

export type TrustPilotBusinessCategoryType = {
  categoryId: string;
  displayName: string;
  isPredicted: boolean;
};

export type TrustPilotReviewType = {
  id: string;
  filtered: boolean;
  isPending: boolean;
  text: string;
  rating: number;
  labels: {
    merge: Record<string, unknown> | null;
    verification: TrustPilotVerificationType;
  };
  title: string;
  likes: number;
  source: string;
  dates: TrustPilotReviewDatesType;
  // Trustpilot returns a variable report payload depending on report source.
  report: Record<string, unknown> | null;
  hasUnhandledReports: boolean;
  consumer: TrustPilotConsumerType;
  reply: TrustPilotReplyType | null;
  consumersReviewCountOnSameDomain: number;
  consumersReviewCountOnSameLocation: number | null;
  productReviews: Record<string, unknown>[];
  language: string;
  location: Record<string, unknown> | null;
  isChosenItem: boolean;
};

export type TrustPilotVerificationType = {
  isVerified: boolean;
  createdDateTime: string;
  reviewSourceName: string;
  verificationSource: string;
  verificationLevel: string;
  hasDachExclusion: boolean;
};

export type TrustPilotReviewDatesType = {
  experienceDate: string;
  publishedDate: string;
  updateDate: string | null;
  submittedDate: string | null;
};

export type TrustPilotConsumerType = {
  id: string;
  displayName: string;
  imageUrl: string;
  numberOfReviews: number;
  countryCode: string;
  hasImage: boolean;
  isVerified: boolean;
};

export type TrustPilotReplyType = {
  message: string;
  publishDate: string;
  updatedDate: string | null;
};

export type GetTrustPilotBusinessDetailsType = {
  data: TrustPilotBusinessApiType;
};

export type GetTrustPilotBusinessReviewsType = {
  business: TrustPilotBusinessApiType;
  reviews: TrustPilotReviewType[];
  pagination: TrustPilotPaginationType;
};

export type TrustPilotBusinessApiType = {
  id: number;
  name: string;
  // Trustpilot API calls use the business domain as the external identifier.
  domain: string;
  logo_url?: string;
  rating: number;
  trust_score: number;
  review_count: number;
  one_star_reviews_count: number;
  two_star_reviews_count: number;
  three_star_reviews_count: number;
  location?: string;
  country?: string;
  website_url?: string;
  trustpilot_url?: string;
  lead_type?: string;
  source?: string;
  raw_data?: {
    logoUrl?: string;
    [key: string]: unknown;
  };
  created_at?: string;
  updated_at?: string;
};

export type TrustPilotBusinessType = TrustPilotBusinessApiType & {
  // Redux slice adds this field when users select reviews; it is client-only.
  // Do not serialize it into API payloads.
  userSelectedReviews?: TrustPilotReviewType[];
};

export type TrustPilotPersistedBusinessPayload = Omit<
  TrustPilotBusinessType,
  "userSelectedReviews"
> & {
  reviews?: TrustPilotReviewType[];
};

export type TrustPilotPaginationType = {
  current_page: number;
  total_pages: number;
  total_reviews: number;
};

export type TrustPilotCartShareBusinessPayload = {
  business_id: string;
  reviews?: TrustPilotReviewType[];
};

// Cart-share payloads intentionally use compact business records because the
// public share endpoint only needs a backend business id and selected reviews.
export type TrustPilotCartShareDataPayload = {
  businesses: TrustPilotCartShareBusinessPayload[];
  coupon_code?: string;
  source?: string;
};

export type TrustPilotCartShareResponse = {
  data: {
    uuid: string;
    payload: TrustPilotCartShareDataPayload;
    created_at: string;
    updated_at: string;
  };
  success: boolean;
};
